/**
 * In-page fragment links (`#heading`, footnote refs/backrefs) in the markdown preview.
 *
 * These are NOT document links: they must never reach the document link resolver
 * (which would toast "Document not found"). Resolve the target inside the preview
 * root and scroll it into view instead.
 *
 * Clobber prefix: rehype-sanitize prefixes every `id` with `user-content-`, but
 * remark-rehype already emitted `user-content-fn-1` / `user-content-fnref-1`.
 * The rendered DOM therefore has ids like `user-content-user-content-fn-1`
 * while hrefs stay `#user-content-fn-1`. Try the id as written, with one extra
 * prefix, and with the prefix stripped.
 */

export const CLOBBER_PREFIX = 'user-content-'

/** Classes applied briefly to the scrolled-to element. */
const HIGHLIGHT_CLASSES = ['bg-primary/10', 'transition-colors', 'rounded-sm']
const HIGHLIGHT_MS = 1600

/** True for pure same-page fragment hrefs (`#foo`, `#user-content-fn-1`); false for `/doc/x#foo`, `x.md#foo`, bare `#`. */
export function isInPageFragmentHref(href: string | null | undefined): boolean {
  if (!href) return false
  const raw = href.trim()
  return raw.startsWith('#') && raw.length > 1
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** Candidate element ids for a fragment href, most specific first, de-duplicated. */
export function getInPageAnchorIdCandidates(href: string): string[] {
  const fragment = safeDecode(href.trim().replace(/^#/, ''))
  if (!fragment) return []
  const candidates = [fragment, `${CLOBBER_PREFIX}${fragment}`]
  if (fragment.startsWith(CLOBBER_PREFIX)) {
    candidates.push(fragment.slice(CLOBBER_PREFIX.length))
  }
  return [...new Set(candidates)]
}

function findById(root: ParentNode, id: string): HTMLElement | null {
  const escaped = id.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  return root.querySelector<HTMLElement>(`[id="${escaped}"]`)
}

/** Find the element a fragment href points at inside `root`, or null. */
export function findInPageAnchorTarget(root: ParentNode, href: string): HTMLElement | null {
  for (const id of getInPageAnchorIdCandidates(href)) {
    const el = findById(root, id)
    if (el) return el
  }
  return null
}

function highlight(el: HTMLElement): void {
  const added = HIGHLIGHT_CLASSES.filter((c) => !el.classList.contains(c))
  el.classList.add(...added)
  window.setTimeout(() => el.classList.remove(...added), HIGHLIGHT_MS)
}

/**
 * Scroll the fragment target into view (nearest scrollable ancestor, so it works
 * inside the document viewer's own scroll container). Returns true if found.
 */
export function scrollToInPageAnchor(root: ParentNode | null | undefined, href: string): boolean {
  const target = findInPageAnchorTarget(root ?? document, href)
  if (!target) return false
  target.scrollIntoView({ block: 'center', behavior: 'smooth' })
  highlight(target)
  return true
}
